import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    NSButton, NSCard, NSInput, NSLabel,
} from 'bricks';
import {
    faClone, faTrash, faDollar, faPercentage,
} from '@fortawesome/free-solid-svg-icons';
import './TenantsOverviewCard.scss';
import { formatUSDCurrencyTwoDigits } from 'ns_libs/formatter';
import useDateInput from 'views/ProFormaTable/hooks/useDateInputs';
import RenderIf from 'components/RenderIf/RenderIf';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import { ITenant } from 'views/ProFormaSetup/types';

interface TenantsOverviewCardProps {
    values: ITenant;
    onDuplicate: () => void;
    onDelete: () => void;
    onUpdate: (updatedValues: TenantsOverviewCardProps['values']) => void;
    count?: number;
    operationStartDate?: Date;
    proFormaSaleDate?: Date;
    isSignupFlow?: boolean;
}

const TenantsOverviewCard = ({
    values, onDuplicate, onDelete, onUpdate, count,
    operationStartDate, proFormaSaleDate, isSignupFlow = false,
}: TenantsOverviewCardProps) => {
    const { dateStrings, handleDateChange } = useDateInput();

    const [editingName, setEditingName] = useState(false);

    const operationStartDateString = operationStartDate ? operationStartDate.toISOString().split('T')[0] : '';
    const proFormaSaleDateString = proFormaSaleDate ? proFormaSaleDate.toISOString().split('T')[0] : '';
    const leaseStartString = values.leaseStart ? new Date(values.leaseStart).toISOString().split('T')[0] : '';
    const rentStartString = values.rentStart ? new Date(values.rentStart).toISOString().split('T')[0] : '';
    const recoveryStartString = values.recoveryStart ? new Date(values.recoveryStart).toISOString().split('T')[0] : '';

    return (
        <NSCard className="NSCard--level-4 mb-2 p-2">
            <div className="d-flex justify-content-between">
                {editingName ? (
                    <input
                        type="text"
                        value={values.tenant || `Tenant ${count}`}
                        onChange={e => onUpdate({ id: values.id, tenant: e.target.value })}
                        onBlur={() => setEditingName(false)}
                        onKeyDown={e => e.key === 'Enter' && setEditingName(false)}
                        className="form-control p-1 w-50"
                    />
                ) : (
                    <div
                        className="TenantsOverviewCard__title cursor--pointer"
                        onClick={() => setEditingName(true)}
                        onKeyDown={e => e.key === 'Enter' && setEditingName(true)}
                        role="button"
                        tabIndex={0}
                    >
                        {values.tenant || 'Default name'}
                    </div>
                )}
                <div>
                    <RenderIf isTrue={!isSignupFlow}>
                        <NSButton className="p-0 mr-2 TenantsOverviewCard__noOutline" callback={onDuplicate}>
                            <FontAwesomeIcon icon={faClone} className="NSNavbar__right__icon-list__icon cursor--pointer" />
                        </NSButton>
                        <NSButton className="p-0 TenantsOverviewCard__noOutline" callback={onDelete}>
                            <FontAwesomeIcon icon={faTrash} className="NSNavbar__right__icon-list__icon cursor--pointer" />
                        </NSButton>
                    </RenderIf>

                    <RenderIf isTrue={isSignupFlow}>
                        <NSButtonBase className="p-0 mr-2 TenantsOverviewCard__noOutline" callback={onDuplicate}>
                            <FontAwesomeIcon icon={faClone} className="NSNavbar__right__icon-list__icon cursor--pointer" />
                        </NSButtonBase>
                        <NSButtonBase className="p-0 TenantsOverviewCard__noOutline" callback={onDelete}>
                            <FontAwesomeIcon icon={faTrash} className="NSNavbar__right__icon-list__icon cursor--pointer" />
                        </NSButtonBase>
                    </RenderIf>
                </div>
            </div>
            <div className="TenantsOverviewCard__separator" />
            <div className="d-flex justify-content-between TenantsOverviewCard__inputContainer">
                <div className="TenantsOverviewCard__inputContainer__input">
                    <NSLabel>Suite</NSLabel>
                    <NSInput
                        id="suite"
                        name="suite"
                        placeholder="Enter suite"
                        type="text"
                        value={values.suite || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({ id: values.id, suite: e.target.value })}
                    />
                </div>
                <div className="TenantsOverviewCard__inputContainer__input">
                    <NSLabel>SF</NSLabel>
                    <NSInput
                        id="averageSquareFootage"
                        name="averageSquareFootage"
                        placeholder="Enter average square footage"
                        type="number"
                        value={Number(values.squareFootage)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({
                            id: values.id,
                            squareFootage: Number(e.target.value),
                            totalRent: Number(e.target.value) * (values.valuePerSfPerYear || 0),
                        })}
                    />
                </div>
            </div>
            <div className="d-flex justify-content-between TenantsOverviewCard__inputContainer pt-2">
                <div className="TenantsOverviewCard__inputContainer__input">
                    <NSLabel>Rent growth</NSLabel>
                    <NSInput
                        id="rentGrowth"
                        name="rentGrowth"
                        placeholder="Enter growth"
                        type="number"
                        value={Number(values.rentGrowth) ? Number(values.rentGrowth) * 100 : 0}
                        appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(
                            { id: values.id, rentGrowth: Number(e.target.value) / 100 },
                        )}
                        max={100}
                    />
                </div>
                <div className="TenantsOverviewCard__inputContainer__input">
                    <NSLabel>Rent/SF/year</NSLabel>
                    <NSInput
                        id="valuePerSfPerYear"
                        name="valuePerSfPerYear"
                        placeholder="Enter..."
                        type="number"
                        value={Number(values.valuePerSfPerYear)}
                        prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate({
                            id: values.id,
                            valuePerSfPerYear: Number(e.target.value),
                            totalRent: Number(values.squareFootage) * Number(e.target.value),
                        })}
                    />
                </div>
            </div>
            <div className="d-flex TenantsOverviewCard__inputContainer pt-2">
                <div className="flex-grow-1 position-relative">
                    <NSLabel>Lease start</NSLabel>
                    <NSInput
                        id="leaseStart"
                        name="leaseStart"
                        type="date"
                        value={dateStrings.leaseStartDate ?? (leaseStartString || '')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleDateChange('leaseStartDate')(e);
                            onUpdate({ id: values.id, leaseStart: e.target.value });
                        }}
                        min={operationStartDateString}
                        max={proFormaSaleDateString}
                    />
                </div>
                <div className="flex-grow-1 position-relative">
                    <NSLabel>Rent start</NSLabel>
                    <NSInput
                        id="rentStartDate"
                        name="rentStartDate"
                        type="date"
                        value={dateStrings.rentStartDate ?? (rentStartString || '')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleDateChange('rentStartDate')(e);
                            onUpdate({ id: values.id, rentStart: e.target.value });
                        }}
                        min={operationStartDateString}
                        max={proFormaSaleDateString}
                    />
                </div>
            </div>
            <div className="TenantsOverviewCard__separator" />
            <div className="d-flex justify-content-between text-white font-weight-bold">
                Annual rent (year 1):
                <div>
                    {values.squareFootage && values.valuePerSfPerYear
                        ? formatUSDCurrencyTwoDigits(values.squareFootage * values.valuePerSfPerYear) : 0}
                </div>
            </div>
            <div className="TenantsOverviewCard__separator" />
            <div className="d-flex TenantsOverviewCard__inputContainer">
                <div className="TenantsOverviewCard__inputContainer__input">
                    <NSLabel>Recovery income %</NSLabel>
                    <NSInput
                        id="recoveryPercentage"
                        name="recoveryPercentage"
                        placeholder="Enter recovery income"
                        type="number"
                        value={Number(values.recoveryPercentage) ? Number(values?.recoveryPercentage) * 100 : 0}
                        appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(
                            { id: values.id, recoveryPercentage: Number(e.target.value) / 100 },
                        )}
                        max={100}
                    />
                </div>
                <div className="TenantsOverviewCard__inputContainer__largeInput">
                    <NSLabel>Recovery start</NSLabel>
                    <NSInput
                        id="recoveryStartDate"
                        name="recoveryStartDate"
                        type="date"
                        value={dateStrings.recoveryStartDate ?? (recoveryStartString || '')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            handleDateChange('recoveryStartDate')(e);
                            onUpdate({ id: values.id, recoveryStart: e.target.value });
                        }}
                        min={operationStartDateString}
                        max={proFormaSaleDateString}
                    />
                </div>
            </div>
            <div className="TenantsOverviewCard__separator" />
            <div className="d-flex justify-content-between text-white font-weight-bold">
                <div>Rec. income amount/year</div>
                <div>
                    {values.squareFootage && values.valuePerSfPerYear
                        ? formatUSDCurrencyTwoDigits(values.squareFootage * values.valuePerSfPerYear) : '-'}
                </div>
            </div>
        </NSCard>
    );
};

export default TenantsOverviewCard;
